import { useSetActiveSection } from "@/components/active-section";
import { PipelineConfigurator } from "@/components/admin/pipeline-configurator";
import { RoleLink, useClientRole } from "@/components/client-role";
import { CandidateDrawer } from "@/components/dashboard/candidate-drawer";
import { CandidateKanban } from "@/components/dashboard/candidate-kanban";
import { CandidateList, type CardFields } from "@/components/dashboard/candidate-list";
import { EditPreviewToggle } from "@/components/dashboard/edit-preview-toggle";
import { EditableField, MiniSelect } from "@/components/dashboard/editable";
import { PageHeader } from "@/components/dashboard/page-header";
import { PAGER_ARROW, Pager } from "@/components/dashboard/pager";
import { PipelineTracker } from "@/components/dashboard/pipeline-tracker";
import { OpeningsBadge, StatusBadge, roleHealth, summarizeOpenings } from "@/components/dashboard/role-status";
import { EmptyState, ErrorState, Skeleton } from "@/components/dashboard/state";
import { useEditPreview } from "@/components/edit-preview";
import { Input } from "@/components/ui/input";
import { Modal } from "@/components/ui/modal";
import { Tooltip } from "@/components/ui/tooltip";
import { adminApi } from "@/lib/admin-api";
import { ApiError } from "@/lib/api";
import { clientApi } from "@/lib/client-api";
import { STALL_DAYS, formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  type CandidateCard,
  type CandidateStatus,
  type OpeningStatus,
  type OpeningSummary,
  type OpeningView,
  type StageDef,
  type Tier,
  stageMeta,
} from "@ow/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useRouterState, useSearch } from "@tanstack/react-router";
import { ArrowUpDown, Check, ChevronLeft, ChevronRight, Columns3, List, Plus, Search, Users, X } from "lucide-react";
import { type FormEvent, useEffect, useMemo, useState } from "react";

// Tier → label for the role's context line (kept local so the page doesn't depend on the home table).
const TIER_LABEL: Record<Tier, string> = { csuite: "C-Suite", vp_director: "VP / Director", general: "General" };

// The client view of a role is a read-only observer surface: comp expectations are shielded (the search
// firm owns that conversation), so the applications list never shows salary — employer stays, as context.
const CLIENT_FIELDS: CardFields = { salary: false, employer: true };

// The role-detail query payload — used to type the optimistic cache patch on a stage/status move.
type RoleDetail = Awaited<ReturnType<typeof clientApi.searchDetail>>;

/** One cell of the facts strip. Every cell shares the same structure and weight — eyebrow label over
 *  a value — so the strip reads as equal facets of a single panel (the same language as the overview
 *  Portfolio strip), and it fills the full page width rather than a left-aligned block. Empty values
 *  show an em dash so the grid never collapses unevenly. */
function Fact({ label, value }: { label: string; value: string | number | null }) {
  return (
    <div className="flex flex-col gap-1.5 p-4">
      <span className="eyebrow whitespace-nowrap">{label}</span>
      <span className="text-sm font-medium tabular-nums text-foreground">
        {value == null || value === "" ? "—" : value}
      </span>
    </div>
  );
}

/** The role-lifecycle statuses an admin sets on a role (distinct from the derived health read the client
 *  sees). Representative options for the design mock. */
const ROLE_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "on_hold", label: "On hold" },
  { value: "closed", label: "Closed" },
];

/** An editable facts-strip cell — same structure and weight as {@link Fact}, but the value is a
 *  hover-to-edit field (admin edit mode). */
function FactEdit({ label, value, onCommit }: { label: string; value: string; onCommit: (v: string) => void }) {
  return (
    <div className="flex flex-col gap-1.5 p-4">
      <span className="eyebrow whitespace-nowrap">{label}</span>
      <EditableField value={value} onCommit={onCommit} placeholder="—" className="text-sm font-medium" />
    </div>
  );
}

/** The page-level Edit ⇄ Preview control — the shared segmented toggle, so the role page and the candidate
 *  drawer flip modes through the identical affordance. Shown only to admins. */
function EditModeToggle({ editing, onToggle }: { editing: boolean; onToggle: () => void }) {
  return <EditPreviewToggle editing={editing} onToggle={onToggle} />;
}

/** Primary "add candidate" action — appears in the applications toolbar (and empty state) in edit mode. */
// Add candidate is a secondary action on this page — reviewing and moving candidates is the point, not
// creating them — so in the toolbar it wears the quiet bordered style, not the solid primary. The empty
// state (no candidates yet) is the one place it's the sole call to action, so it opts into `primary`.
function AddCandidateButton({
  onClick,
  variant = "secondary",
}: { onClick: () => void; variant?: "primary" | "secondary" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md px-3 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring",
        variant === "primary"
          ? "bg-foreground text-background transition-opacity hover:opacity-90"
          : "border bg-card text-foreground transition-colors hover:bg-muted",
      )}
    >
      <Plus className="size-4" /> Add candidate
    </button>
  );
}

/** Add candidate — a single-pass dialog that drops a new person into THIS role's pipeline (name is all
 *  that's required; title/company/starting stage flesh out the row). Mirrors the team Invite dialog so the
 *  two creation flows read as one system. Its own component so the draft resets cleanly on close. */
function AddCandidateDialog({
  open,
  onOpenChange,
  ladder,
  onAdd,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ladder: StageDef[];
  onAdd: (v: { fullName: string; currentTitle: string; currentEmployer: string; stage: string }) => void;
}) {
  const firstStage = ladder[0]?.key ?? "";
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [company, setCompany] = useState("");
  const [stage, setStage] = useState(firstStage);
  const valid = name.trim().length > 0;

  const close = (o: boolean) => {
    if (!o) {
      setName("");
      setTitle("");
      setCompany("");
      setStage(firstStage);
    }
    onOpenChange(o);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    onAdd({ fullName: name.trim(), currentTitle: title.trim(), currentEmployer: company.trim(), stage });
    close(false);
  };

  return (
    <Modal
      open={open}
      onOpenChange={close}
      className="w-[460px]"
      title="Add candidate"
      description="Drop someone into this role's pipeline. You can flesh out their profile afterwards."
    >
      <form onSubmit={submit} className="mt-4 space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="cand-name" className="field-label">
            Full name
          </label>
          {/* Radix Dialog focuses the first field (this input) on open, so no explicit autoFocus. */}
          <Input
            id="cand-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Jordan Ellis"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label htmlFor="cand-title" className="field-label">
              Current title
            </label>
            <Input
              id="cand-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. VP Finance"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="cand-company" className="field-label">
              Current company
            </label>
            <Input
              id="cand-company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="e.g. Meridian"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <span className="field-label">Starting stage</span>
          <MiniSelect
            ariaLabel="Starting stage"
            value={stage}
            onChange={setStage}
            options={ladder.map((s) => ({ value: s.key, label: s.label }))}
            fullWidth
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={() => close(false)}
            className="inline-flex h-9 items-center rounded-md border bg-card px-3.5 text-sm font-medium text-foreground outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!valid}
            className="inline-flex h-9 items-center rounded-md bg-foreground px-3.5 text-sm font-medium text-background outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
          >
            Add candidate
          </button>
        </div>
      </form>
    </Modal>
  );
}

// A seat's status colour, as a single cell — the same "colour is the signal" rule the status pills use.
const SEAT_DOT: Record<OpeningStatus, string> = {
  open: "bg-foreground/35",
  filled: "bg-[var(--color-success)]",
  on_hold: "bg-[var(--color-warning)]",
  cancelled: "bg-foreground/20",
};

// Seats cluster by status along the bar: filled first, then open, then on hold.
const SEAT_ORDER: Record<OpeningStatus, number> = { filled: 0, open: 1, on_hold: 2, cancelled: 3 };

/** The role's headcount, as an "Openings" eyebrow with the exact-count split ("the matrix") right-aligned
 *  on the same header row, over a full-width capacity bar split into one cell per seat. Shown only when a
 *  role fills more than one seat, so single-hire roles stay clean. The seats are identical (same role, same
 *  designation), so they read as fungible capacity: equal-width cells, one per opening, clustered filled →
 *  open → on hold, spanning the full width at every headcount — one consistent treatment for any N. The
 *  count label always carries the numbers; each cell's detail — including who took that filled seat —
 *  surfaces on hover. Cancelled seats are left out. */
function OpeningsPanel({ openings, summary }: { openings: OpeningView[]; summary: OpeningSummary }) {
  const countOf = (s: OpeningStatus) => openings.filter((o) => o.status === s).length;
  const breakdown = (["filled", "open", "on_hold"] as const)
    .map((status) => ({ status, count: countOf(status) }))
    .filter((s) => s.count > 0)
    .map((s) => ({ n: s.count, word: s.status === "on_hold" ? "on hold" : s.status }));
  // Total first, then the status split. Rendered mono uppercase (data readout): numerals foreground,
  // unit words muted, so the quantities carry. `label` is the plain-text mirror for aria/hover.
  const parts = [{ n: summary.total, word: "seats" }, ...breakdown];
  const label = breakdown.map((p) => `${p.n} ${p.word}`).join(" · ");
  const seats = openings
    .filter((o) => o.status !== "cancelled")
    .slice()
    .sort((a, b) => SEAT_ORDER[a.status] - SEAT_ORDER[b.status]);
  const seatTitle = (o: OpeningView) =>
    o.status === "filled"
      ? o.filledByName
        ? `Filled — ${o.filledByName}`
        : "Filled"
      : o.status === "on_hold"
        ? "On hold"
        : "Open";

  return (
    <div className="flex flex-col gap-2 border-t p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="eyebrow">Openings</span>
        <span className="font-mono text-xs uppercase tracking-[0.04em] text-muted-foreground">
          {parts.map((p, i) => (
            <span key={p.word}>
              {i > 0 ? <span className="px-1 text-muted-foreground/60">·</span> : null}
              <span className="text-foreground">{p.n}</span> {p.word}
            </span>
          ))}
        </span>
      </div>
      <span
        className="flex h-2 w-full items-stretch gap-0.5"
        role="img"
        aria-label={`${summary.total} seats · ${label}`}
      >
        {seats.map((o) => (
          <span key={o.id} className={cn("flex-1 rounded-full", SEAT_DOT[o.status])} title={seatTitle(o)} />
        ))}
      </span>
    </div>
  );
}

type CandidateView = "list" | "board";

/** Segmented list / pipeline-board switch for the applications section. */
function ViewToggle({ value, onChange }: { value: CandidateView; onChange: (v: CandidateView) => void }) {
  const opts = [
    { v: "list" as const, icon: List, label: "List view" },
    { v: "board" as const, icon: Columns3, label: "Board view" },
  ];
  return (
    <div className="flex items-center gap-0.5 rounded-lg border bg-card p-0.5">
      {opts.map(({ v, icon: Icon, label }) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          aria-label={label}
          aria-pressed={value === v}
          className={cn(
            "grid size-7 place-items-center rounded-md outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
            value === v
              ? "bg-foreground text-background shadow-sm"
              : "text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground",
          )}
        >
          <Icon className="size-4" />
        </button>
      ))}
    </div>
  );
}

const CAND_STATUS_ORDER: CandidateStatus[] = ["active", "on_hold", "withdrawn", "rejected", "placed"];
const CAND_STATUS_LABEL: Record<CandidateStatus, string> = {
  active: "Active",
  on_hold: "On hold",
  withdrawn: "Withdrawn",
  rejected: "Rejected",
  placed: "Placed",
};

// Sort dimensions for the candidate slate — the ones a client scans a long list by: who's furthest
// along, who's been sitting longest (surfaces stalls), or plain alphabetical to find a name.
type CandSort = "stage" | "time" | "az";
const CAND_SORT_OPTS: { v: CandSort; label: string }[] = [
  { v: "stage", label: "Furthest stage" },
  { v: "time", label: "Longest in stage" },
  { v: "az", label: "Name A–Z" },
];

/** A pill toggle for the applications status filter (applies to both the list and the board). */
function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-full border px-2.5 py-1 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
        active
          ? "border-transparent bg-foreground text-background shadow-sm"
          : "border-border bg-card text-muted-foreground hover:border-foreground/30 hover:bg-foreground/[0.04] hover:text-foreground",
      )}
    >
      {label}
    </button>
  );
}

/** Compact sort control for the candidate slate — a small menu so a client can re-order a long list
 *  (furthest along · longest in stage · A–Z) without hunting. Same size/shape as the home table's Sort
 *  control so the two toolbars are one design. */
function SortMenu({ value, onChange }: { value: CandSort; onChange: (v: CandSort) => void }) {
  const [open, setOpen] = useState(false);
  const current = CAND_SORT_OPTS.find((o) => o.v === value);
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex h-9 items-center gap-1.5 rounded-md border bg-card px-2.5 text-sm text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ArrowUpDown className="size-3.5" />
        <span className="hidden sm:inline">{value === "stage" ? "Sort" : current?.label}</span>
      </button>
      {open ? (
        <>
          <button
            type="button"
            aria-label="Close sort menu"
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 z-20 mt-1 w-44 rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
            <p className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">Sort by</p>
            {CAND_SORT_OPTS.map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => {
                  onChange(o.v);
                  setOpen(false);
                }}
                className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm outline-none hover:bg-accent focus-visible:bg-accent"
              >
                {o.label}
                {value === o.v ? <Check className="size-3.5 text-foreground" /> : null}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

// Mirrors the real layout (header → summary strip → sections) so nothing shifts on load — the same
// skeleton discipline as the home page.
function DetailSkeleton() {
  return (
    <div className="space-y-8">
      <div className="space-y-6">
        <div className="space-y-3">
          <Skeleton className="h-8 w-80" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
      <Skeleton className="h-40 w-full rounded-xl" />
      <Skeleton className="h-64 w-full rounded-xl" />
    </div>
  );
}

export function SearchDetailPage() {
  const { searchId } = useParams({ strict: false }) as { searchId: string };
  const navigate = useNavigate();
  // The open candidate lives in the URL (`?candidate=<id>`) so the drawer is deep-linkable and the
  // browser back button closes it. Prev/next just rewrites the param. We navigate to the concrete
  // current pathname (not relative ".") so switching the param to a new id resolves reliably.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const drawerSearch = useSearch({ strict: false }) as { candidate?: string; from?: string };
  // Where the user arrived from (Needs attention, a tier, …) — carried across the sibling-role pager so
  // paging between roles keeps the same "back" target. The top bar reads it to offer a context-aware back.
  const from = drawerSearch.from;
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["client", "search", searchId],
    queryFn: () => clientApi.searchDetail(searchId),
  });

  useSetActiveSection(data?.tier);
  const role = useClientRole();

  // Stage/status writes for the applications list (D2) — admin only; the client view is read-only and
  // never wires these controls. Same audited endpoint the back-office uses; on success we invalidate
  // the role query so the tracker and list both reflect the move. The control lives inline on each
  // application row — no separate duplicate "manage" panel.
  const qc = useQueryClient();
  const move = useMutation({
    mutationFn: (v: { linkId: string; stage?: string; status?: CandidateStatus }) =>
      adminApi.links.moveStage(v.linkId, { stage: v.stage, status: v.status }),
    // Optimistically move the candidate in-cache so the board AND the pipeline stepper counts update in
    // lockstep the instant a card drops (a drag was leaving the tracker a refetch behind). Rolled back on
    // error; reconciled with the server on settle.
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: ["client", "search", searchId] });
      const prev = qc.getQueryData<RoleDetail>(["client", "search", searchId]);
      if (prev) {
        const from = prev.candidates.find((c) => c.linkId === v.linkId)?.stage;
        qc.setQueryData<RoleDetail>(["client", "search", searchId], {
          ...prev,
          candidates: prev.candidates.map((c) =>
            c.linkId === v.linkId
              ? {
                  ...c,
                  ...(v.stage ? { stage: v.stage, daysInStage: 0 } : {}),
                  ...(v.status ? { status: v.status } : {}),
                }
              : c,
          ),
          stages:
            v.stage && from && from !== v.stage
              ? prev.stages.map((s) =>
                  s.key === from
                    ? { ...s, count: Math.max(0, s.count - 1) }
                    : s.key === v.stage
                      ? { ...s, count: s.count + 1 }
                      : s,
                )
              : prev.stages,
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["client", "search", searchId], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["client", "search", searchId] }),
  });
  // The "Saved" confirmation is transient — clear it a few seconds after a successful move so the
  // toolbar doesn't carry a stale green tick indefinitely (audit design-major).
  useEffect(() => {
    if (!move.isSuccess) return;
    const t = setTimeout(() => move.reset(), 2500);
    return () => clearTimeout(t);
  }, [move.isSuccess, move.reset]);

  const [view, setView] = useState<CandidateView>(() => {
    try {
      return localStorage.getItem("ow:candidateView") === "board" ? "board" : "list";
    } catch {
      return "list";
    }
  });
  const [selectedStage, setSelectedStage] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<CandidateStatus | "all">("all");
  const [stalledOnly, setStalledOnly] = useState(false);
  const [nameQuery, setNameQuery] = useState("");
  const [sort, setSort] = useState<CandSort>("stage");
  // Add candidate — the dialog's open state and the session-local additions it produces (this mock has no
  // persistence; dev wires the real quick-add). New people are merged into the live slate below.
  const [addOpen, setAddOpen] = useState(false);
  const [addedCandidates, setAddedCandidates] = useState<CandidateCard[]>([]);
  // Admin edit mode (D2): the same page turns editable for OW staff; "Preview as client" flips it back to
  // the read-only render. The Edit ⇄ Preview state is SHARED with the candidate drawer (one context), so the
  // two never disagree, and admins land in Preview by default. Session-local edits live in `ov` (held here,
  // not persisted — wiring is a dev follow-up), mirroring the candidate drawer.
  const { previewClient, toggle: togglePreview } = useEditPreview();
  const [ov, setOv] = useState<Record<string, string>>({});
  const editing = role === "admin" && !previewClient;
  const put = (k: string) => (v: string) => setOv((o) => ({ ...o, [k]: v }));
  const get = (k: string, fallback?: string | null) => ov[k] ?? fallback ?? "";
  const changeView = (v: CandidateView) => {
    setView(v);
    // The board shows every stage, so a single-stage filter doesn't apply there.
    if (v === "board") setSelectedStage(null);
    try {
      localStorage.setItem("ow:candidateView", v);
    } catch {
      // non-persistent is fine
    }
  };

  // Sibling searches in the same tier, for the prev/next pager (recent-first, as on the tier page).
  const tierForList = data?.tier;
  const { data: tierSearches } = useQuery({
    queryKey: ["client", "searches", tierForList],
    queryFn: () => clientApi.searches(tierForList as Tier),
    enabled: !!tierForList,
  });
  const searchOrder = useMemo(
    () =>
      tierSearches
        ? [...tierSearches].sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "")).map((s) => s.id)
        : [],
    [tierSearches],
  );

  if (isLoading) return <DetailSkeleton />;
  if (isError) return <ErrorState onRetry={() => refetch()}>Couldn't load this role.</ErrorState>;
  if (!data) return <EmptyState>Role not found.</EmptyState>;

  // Two counts, deliberately: `candidateCount` is every applicant ever linked (drives whether the
  // section and its toolbar exist at all); `activeCount` is those still live in the funnel — the one
  // number the facts strip and the pipeline caption show, so it always matches the tracker's live
  // stage counts and the overview's "active applications" for the same role (audit product-critical).
  // Session-local additions (from the Add-candidate dialog) ride at the front of the real slate, so the
  // whole page — counts, chips, sort, drawer — sees them as ordinary candidates.
  const allCandidates = addedCandidates.length > 0 ? [...addedCandidates, ...data.candidates] : data.candidates;
  const candidateCount = allCandidates.length;
  const activeCount = allCandidates.filter((c) => c.status === "active").length;
  const handleAddCandidate = (v: {
    fullName: string;
    currentTitle: string;
    currentEmployer: string;
    stage: string;
  }) => {
    const id = crypto.randomUUID();
    setAddedCandidates((prev) => [
      {
        linkId: id,
        candidateId: id,
        fullName: v.fullName,
        currentTitle: v.currentTitle || null,
        currentEmployer: v.currentEmployer || null,
        stage: v.stage,
        status: "active",
        daysInStage: 0,
        salaryExpectation: null,
        currency: data.budgetCurrency,
        noticePeriodDays: null,
      },
      ...prev,
    ]);
  };
  // Headcount roll-up for this role. `multiSeat` gates every openings affordance on the page: a
  // single-seat role's seat is implicit, so it shows none of them (chip, breakdown, per-card seats).
  const openingSummary = summarizeOpenings(data.openings);
  const multiSeat = openingSummary.total > 1;
  const ladder = data.pipeline;
  const rankOf = (key: string) => ladder.findIndex((s) => s.key === key);
  // The client's chosen order (default: most-advanced first, by THIS role's ladder). "Longest in stage"
  // surfaces stalls to the top; "A–Z" is for finding a name. Same order feeds the list and the board.
  const orderedCandidates = [...allCandidates].sort((a, b) => {
    if (sort === "az") return a.fullName.localeCompare(b.fullName);
    if (sort === "time") return b.daysInStage - a.daysInStage;
    return rankOf(b.stage) - rankOf(a.stage);
  });

  // Name/employer search (audit K2) is applied FIRST so the status chip counts below reflect the
  // current search — a chip that reads "Active (3)" always matches what the list actually shows.
  const q = nameQuery.trim().toLowerCase();
  const bySearch = q
    ? orderedCandidates.filter(
        (c) =>
          c.fullName.toLowerCase().includes(q) ||
          (c.currentEmployer ?? "").toLowerCase().includes(q) ||
          (c.currentTitle ?? "").toLowerCase().includes(q),
      )
    : orderedCandidates;

  // Status filter — the board's one filter dimension (stage is its columns), shared with the list.
  // Counts are taken over the search-filtered set so the chips and the list never disagree.
  const statusCounts = new Map<CandidateStatus, number>();
  for (const c of bySearch) statusCounts.set(c.status, (statusCounts.get(c.status) ?? 0) + 1);
  const statusOptions = CAND_STATUS_ORDER.filter((s) => statusCounts.has(s));
  const activeStatus = statusFilter !== "all" && statusCounts.has(statusFilter) ? statusFilter : "all";
  const byStatus = activeStatus === "all" ? bySearch : bySearch.filter((c) => c.status === activeStatus);
  // "Needs attention" quick filter — active candidates past the stall threshold. A momentum-first cut a
  // client reaches for on a long slate ("just show me who's stuck"), composing on top of status/search.
  const isStalled = (c: (typeof bySearch)[number]) => c.status === "active" && c.daysInStage > STALL_DAYS;
  const stalledCount = bySearch.filter(isStalled).length;
  const byName = stalledOnly ? byStatus.filter(isStalled) : byStatus;
  // Stage filter (list view only) composes on top.
  const shown = selectedStage ? byName.filter((c) => c.stage === selectedStage) : byName;

  // Toolbar derivations. The count matches whatever is actually rendered (the board ignores the
  // single-stage filter, so it counts byName; the list counts the stage-filtered `shown`). Chips only
  // appear when they'd do something: >1 status, any stall, or an active stage filter from the tracker.
  const displayedCount = view === "board" ? byName.length : shown.length;
  const showStatusChips = candidateCount > 0 && (statusOptions.length > 1 || stalledCount > 0);
  const showStageChip = candidateCount > 0 && view === "list" && selectedStage != null;
  const showChips = showStatusChips || showStageChip;

  // Candidate drawer: the currently-displayed slate is what prev/next pages through; the card gives the
  // drawer its stage/status/days instantly (looked up across the whole slate so a filtered-out candidate
  // still resolves). Opening pushes a history entry (back closes); switching/closing replaces it.
  const openCandidateId = drawerSearch.candidate ?? null;
  const drawerOrder = (view === "board" ? byName : shown).map((x) => x.candidateId);
  const drawerCard = openCandidateId ? (allCandidates.find((x) => x.candidateId === openCandidateId) ?? null) : null;
  const goToCandidate = (id: string | null) =>
    navigate({
      to: pathname,
      search: (prev: Record<string, unknown>) => ({ ...prev, candidate: id ?? undefined }),
      replace: openCandidateId != null,
    } as never);

  // Prev/next search within the tier.
  const sIdx = searchOrder.indexOf(searchId);
  const showPager = searchOrder.length > 1 && sIdx >= 0;
  const prevSearch = sIdx > 0 ? (searchOrder[sIdx - 1] ?? null) : null;
  const nextSearch = sIdx >= 0 && sIdx < searchOrder.length - 1 ? (searchOrder[sIdx + 1] ?? null) : null;

  // Tier + function are the role's identity → the header's one-line context. The lead consultant is the
  // client's single point of contact, so it earns a cell in the summary strip rather than this line.
  const context = [`${TIER_LABEL[data.tier]} search`, data.function].filter(Boolean).join(" · ");

  return (
    <div className="animate-fade-up space-y-8">
      {/* Identity zone — the SAME title-first PageHeader as every other page, so the heading sits at the
          exact same height on every screen (no drop when you drill in from the overview — a standing rule).
          Sibling-role paging and the admin edit toggle live in the header's right-hand action slot, not a
          stacked row above the title; the global "back" is the persistent top bar. */}
      <div className="space-y-6">
        <PageHeader
          title={
            <span className="flex flex-wrap items-center gap-3">
              {data.title}
              <OpeningsBadge openings={openingSummary} className="px-2 py-1 text-xs" />
            </span>
          }
          description={context}
          crumbLabel={data.title}
        >
          {showPager ? (
            <Pager
              noun="Role"
              index={sIdx}
              total={searchOrder.length}
              prev={
                prevSearch ? (
                  <RoleLink
                    to="/search/$searchId"
                    params={{ searchId: prevSearch }}
                    search={from ? { from } : undefined}
                    aria-label="Previous role"
                    className={PAGER_ARROW}
                  >
                    <ChevronLeft className="size-4" />
                  </RoleLink>
                ) : null
              }
              next={
                nextSearch ? (
                  <RoleLink
                    to="/search/$searchId"
                    params={{ searchId: nextSearch }}
                    search={from ? { from } : undefined}
                    aria-label="Next role"
                    className={PAGER_ARROW}
                  >
                    <ChevronRight className="size-4" />
                  </RoleLink>
                ) : null
              }
            />
          ) : null}
          {role === "admin" ? <EditModeToggle editing={editing} onToggle={togglePreview} /> : null}
        </PageHeader>

        {/* Summary strip — equal divided cells, same language as the home Portfolio strip, scoped to
            this role: who's running it, then the progress read (how many live, how many closing, when
            it opened). "Furthest stage" is intentionally NOT here — the pipeline tracker below shows it.
            In edit mode the two admin-owned facts (lead consultant, opened) become hover-to-edit; the two
            derived counts stay read-only. */}
        {/* Facts card. The four key facts sit in a divided grid; when the role fills more than one seat,
            the headcount rides along as a full-width row beneath them — same card, same eyebrow-over-value
            language — rather than a separate, half-empty band. A single-hire role shows no such row. */}
        <div className="overflow-hidden rounded-xl border bg-card">
          <div className="grid grid-cols-2 divide-y sm:grid-cols-5 sm:divide-x sm:divide-y-0">
            {editing ? (
              <FactEdit label="Search lead" value={get("owner", data.ownerName)} onCommit={put("owner")} />
            ) : (
              <Fact label="Search lead" value={get("owner", data.ownerName)} />
            )}
            <Fact label="Active candidates" value={activeCount} />
            <Fact label="In final stages" value={data.nearClosure} />
            {editing ? (
              <FactEdit label="Opened" value={get("opened", formatDate(data.dateOpened))} onCommit={put("opened")} />
            ) : (
              <Fact label="Opened" value={formatDate(data.dateOpened)} />
            )}
            {/* Status — a role fact, so it lives in the strip with the others instead of crammed into the
                header corner. Admin sets the lifecycle status; the client reads the derived health badge. */}
            <div className="flex flex-col gap-1.5 p-4">
              <span className="eyebrow whitespace-nowrap">Status</span>
              {editing ? (
                <MiniSelect
                  ariaLabel="Role status"
                  value={get("status", "active")}
                  onChange={put("status")}
                  options={ROLE_STATUS_OPTIONS}
                />
              ) : (
                <div>
                  <StatusBadge h={roleHealth(data)} />
                </div>
              )}
            </div>
          </div>
          {multiSeat ? <OpeningsPanel openings={data.openings} summary={openingSummary} /> : null}
        </div>
      </div>

      {/* Pipeline — a bare card, no section header (the stepper is self-evident; in list view clicking a
          stage filters the slate below). The stepper IS the pipeline view — stages AND live counts. Once a
          role is live its ladder is locked, but we DON'T spend a visible note on that: an admin only learns
          it on hover (native tooltip), and the stages stay clickable as slate filters. A draft shows the
          editor below instead. */}
      <Tooltip
        content={
          role === "admin" && data.launchedAt
            ? "Editing isn't available — this role is live, so its stages are locked. Click a stage to filter the candidates below."
            : null
        }
      >
        <div className="rounded-xl border bg-card px-5 py-6">
          <PipelineTracker
            stages={data.stages}
            leadKey={data.leadStage}
            selectedKey={selectedStage}
            onStageSelect={view === "list" && candidateCount > 0 ? setSelectedStage : undefined}
          />
        </div>
      </Tooltip>

      {/* A draft role (pre-launch) shows the pipeline EDITOR beneath the tracker — add/rename/reorder/
          launch. Once launched the ladder is fixed, so there's no editor, only the locked badge above.
          Viewers never see either. */}
      {role === "admin" && !data.launchedAt ? (
        <PipelineConfigurator searchId={data.id} initialStages={data.pipeline} />
      ) : null}

      {/* Candidates — the page's primary content, built as the twin of the home roles block: no section
          header, ONE toolbar row (filter chips + count on the left; search + sort grouped right; a
          divider, then the list/board toggle), then the table or board beneath it. */}
      <section className="space-y-4">
        {/* Candidates block is a single toolbar row — no separate section label (it would add a second row
            and just echo the page). Add candidate is the icon-only "+" pinned to the far right of this row,
            secondary weight, edit-only: always visible, never buried in a scroll, never reflowing the filters. */}
        {candidateCount > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-1 flex-wrap items-center gap-2">
              {showChips ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  {showStatusChips ? (
                    <>
                      <FilterChip
                        label={`All (${bySearch.length})`}
                        active={activeStatus === "all" && !stalledOnly}
                        onClick={() => {
                          setStatusFilter("all");
                          setStalledOnly(false);
                        }}
                      />
                      {statusOptions.map((s) => (
                        <FilterChip
                          key={s}
                          label={`${CAND_STATUS_LABEL[s]} (${statusCounts.get(s) ?? 0})`}
                          active={!stalledOnly && activeStatus === s}
                          onClick={() => {
                            setStalledOnly(false);
                            setStatusFilter(s);
                          }}
                        />
                      ))}
                      {stalledCount > 0 ? (
                        <>
                          <span className="mx-0.5 h-4 w-px bg-border" />
                          <button
                            type="button"
                            onClick={() => setStalledOnly((v) => !v)}
                            aria-pressed={stalledOnly}
                            className={cn(
                              "rounded-full border px-2.5 py-1 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
                              stalledOnly
                                ? "border-transparent bg-[var(--color-warning)] text-[var(--brand-accent-foreground)] shadow-sm"
                                : "border-[color-mix(in_oklab,var(--color-warning)_35%,transparent)] bg-[color-mix(in_oklab,var(--color-warning)_10%,transparent)] text-[var(--color-warning)] hover:bg-[color-mix(in_oklab,var(--color-warning)_16%,transparent)]",
                            )}
                          >
                            Needs attention ({stalledCount})
                          </button>
                        </>
                      ) : null}
                    </>
                  ) : null}
                  {showStageChip && selectedStage ? (
                    <>
                      {showStatusChips ? <span className="mx-0.5 h-4 w-px bg-border" /> : null}
                      <button
                        type="button"
                        onClick={() => setSelectedStage(null)}
                        aria-label={`Clear ${stageMeta(ladder, selectedStage).label} stage filter`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-foreground/25 bg-foreground/[0.06] px-2.5 py-1 text-xs font-medium text-foreground outline-none transition-colors hover:bg-foreground/10 focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {stageMeta(ladder, selectedStage).label}
                        <X className="size-3" />
                      </button>
                    </>
                  ) : null}
                </div>
              ) : null}
              {/* The status chips already carry counts (All (N), Active (N), …), so a separate total would
                  just echo the active chip. Show a standalone count ONLY when there are no status chips —
                  a single-status role — where nothing else states the total. */}
              {!showStatusChips ? (
                <span className="font-mono text-xs uppercase tracking-[0.04em] text-muted-foreground">
                  <span className="font-medium tabular-nums text-foreground">{displayedCount}</span>{" "}
                  {displayedCount === 1 ? "candidate" : "candidates"}
                </span>
              ) : null}
              {move.isPending || move.isError || move.isSuccess ? (
                <span className="inline-flex min-h-5 items-center text-xs" aria-live="polite">
                  {move.isPending ? (
                    <span className="text-muted-foreground">Saving…</span>
                  ) : move.isError ? (
                    <span className="text-destructive">
                      {move.error instanceof ApiError ? move.error.message : "Couldn't save — try again."}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[var(--color-success)]">
                      <Check className="size-3.5" /> Saved
                    </span>
                  )}
                </span>
              ) : null}
              <div className="relative ml-auto min-w-[180px] max-w-xs flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={nameQuery}
                  onChange={(e) => setNameQuery(e.target.value)}
                  placeholder="Search candidates…"
                  aria-label="Search candidates by name, title or employer"
                  className="h-9 w-full rounded-md border bg-card pl-9 pr-8 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
                />
                {q ? (
                  <button
                    type="button"
                    onClick={() => setNameQuery("")}
                    aria-label="Clear candidate search"
                    className="absolute right-2 top-1/2 grid size-5 -translate-y-1/2 place-items-center rounded text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <X className="size-4" />
                  </button>
                ) : null}
              </div>
              <SortMenu value={sort} onChange={setSort} />
            </div>
            <span className="hidden h-6 w-px shrink-0 bg-border sm:block" />
            <ViewToggle value={view} onChange={changeView} />
            {editing ? (
              <Tooltip content="Add candidate">
                <button
                  type="button"
                  onClick={() => setAddOpen(true)}
                  aria-label="Add candidate"
                  className="grid size-9 shrink-0 place-items-center rounded-md border bg-card text-foreground outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Plus className="size-4" />
                </button>
              </Tooltip>
            ) : null}
          </div>
        ) : null}

        {candidateCount === 0 ? (
          editing ? (
            <div className="flex flex-col items-center gap-4">
              <EmptyState icon={Users}>No candidates yet.</EmptyState>
              <AddCandidateButton onClick={() => setAddOpen(true)} variant="primary" />
            </div>
          ) : (
            <EmptyState icon={Users}>No candidates yet.</EmptyState>
          )
        ) : byName.length === 0 ? (
          <EmptyState icon={Users}>No candidates match your filters.</EmptyState>
        ) : view === "board" ? (
          <CandidateKanban
            candidates={byName}
            ladder={ladder}
            searchId={data.id}
            budgetMin={null}
            budgetMax={null}
            budgetCurrency={data.budgetCurrency}
            fields={CLIENT_FIELDS}
            onOpenCandidate={goToCandidate}
            onMove={editing ? (linkId, patch) => move.mutate({ linkId, ...patch }) : undefined}
          />
        ) : shown.length === 0 ? (
          <EmptyState icon={Users}>
            No candidates at {selectedStage ? stageMeta(ladder, selectedStage).label : "this stage"}.
          </EmptyState>
        ) : (
          <CandidateList
            candidates={shown}
            ladder={ladder}
            searchId={data.id}
            budgetMin={null}
            budgetMax={null}
            budgetCurrency={data.budgetCurrency}
            fields={CLIENT_FIELDS}
            onMove={editing ? (linkId, patch) => move.mutate({ linkId, ...patch }) : undefined}
            onOpenCandidate={goToCandidate}
          />
        )}
      </section>

      {/* Candidate drawer — a right-side slide-over over this page (replaces the old full candidate
          page). Opens from any row/card; pages through the current slate; closes on Esc, scrim, or back. */}
      {openCandidateId ? (
        <CandidateDrawer
          candidateId={openCandidateId}
          searchId={data.id}
          ladder={ladder}
          card={drawerCard}
          order={drawerOrder}
          onNavigate={goToCandidate}
        />
      ) : null}

      <AddCandidateDialog open={addOpen} onOpenChange={setAddOpen} ladder={ladder} onAdd={handleAddCandidate} />
    </div>
  );
}
